import React, { createContext, useContext, useState, useEffect, useLayoutEffect } from 'react';
import {
  getAllEnvironments,
  Environment,
} from '../services/getAllEnvironment.service';
import { authService } from '../services/auth.service';

interface EnvironmentContextType {
  environments: Environment[];
  selectedEnvironmentId: number | '';
  setSelectedEnvironmentId: React.Dispatch<React.SetStateAction<number | ''>>;
  isAdmin: boolean;
}

const EnvironmentContext = createContext<EnvironmentContextType | undefined>(undefined);

const SESSION_KEY = 'global_environmentId';
const REFRESH_KEY = 'global_env_isRefreshing';

export const EnvironmentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const currentUser = authService.getStoredUser();
  const isAdmin = currentUser?.role?.name === 'ADMIN';

  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<number | ''>(() => {
    const saved = sessionStorage.getItem(SESSION_KEY);
    return saved ? Number(saved) : '';
  });

  // Fetch environments once
  useLayoutEffect(() => {
    if (isAdmin) {
      getAllEnvironments()
        .then((envs) => setEnvironments(envs))
        .catch((err) => console.error('Error fetching environments:', err));
    }
  }, [isAdmin]);

  // Persist environment selection to sessionStorage
  useEffect(() => {
    if (selectedEnvironmentId !== '') {
      sessionStorage.setItem(SESSION_KEY, String(selectedEnvironmentId));
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, [selectedEnvironmentId]);

  // Handle refresh vs navigation for environment persistence
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem(REFRESH_KEY, 'true');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    sessionStorage.removeItem(REFRESH_KEY);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (!sessionStorage.getItem(REFRESH_KEY)) {
        sessionStorage.removeItem(SESSION_KEY);
      }
    };
  }, []);

  return (
    <EnvironmentContext.Provider
      value={{
        environments,
        selectedEnvironmentId,
        setSelectedEnvironmentId,
        isAdmin,
      }}
    >
      {children}
    </EnvironmentContext.Provider>
  );
};

export const useEnvironment = (): EnvironmentContextType => {
  const context = useContext(EnvironmentContext);
  if (!context) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider');
  }
  return context;
};

export default EnvironmentContext;
